import { MultiSelectProps } from '@mantine/core';
import { MultiSelect } from 'src/design/atoms/MultiSelect';
import React from 'react';
import { UserDto } from 'src/api/FiretrolApiClient';
import { useJobUsers } from 'src/api/queries/jobs/useJobUsers';
import { toMultiSelectValue } from 'src/design/atoms/toMultiSelectValue';

// Generic user type that works with both legacy IUser and UserDto
export interface IGenericUser {
  id: number;
  full_name?: string;
  fullName?: string;
}

export interface IJobUserSelectProps
  extends Omit<MultiSelectProps, 'data' | 'value' | 'onChange'> {
  jobId: number;
  onChange: (value: UserDto[]) => void;
  onCustomChange?: (value: UserDto[]) => void;
  value: IGenericUser[];
  customValues?: IGenericUser[];
}

// Helper to get full name from either format
const getFullName = (user: IGenericUser): string => {
  return user.full_name ?? user.fullName ?? '';
};

export const JobUserSelect2: React.FC<IJobUserSelectProps> = ({
  onChange,
  jobId,
  onCustomChange,
  customValues = [],
  disabled = false,
  value,
  placeholder = 'Select users...',
  ...multiSelectProps
}) => {
  const { data: options = [] } = useJobUsers(jobId);

  const handleChange = (selectedIds: string[]) => {
    // Separate regular users from custom users
    const regularUsers = selectedIds
      .filter((id) => parseInt(id) > 0)
      .map((id) => options.find((user) => user.id.toString() === id))
      .filter((user): user is UserDto => user !== undefined);

    const customUsers = selectedIds
      .filter((id) => parseInt(id) < 0)
      .map((id) => options.find((user) => user.id.toString() === id))
      .filter((user): user is UserDto => user !== undefined);

    onChange(regularUsers);
    if (onCustomChange) {
      onCustomChange(customUsers);
    }
  };

  // Create data array with both regular and custom users
  const safeCustomValues = customValues ?? [];
  const data = [
    ...options.map((user) => ({
      value: user.id.toString(),
      label: user.fullName,
    })),
    ...safeCustomValues.map((user) => ({
      value: user.id.toString(),
      label: getFullName(user),
    })),
  ];

  // Get selected values as string IDs, avoiding duplicates
  const selectedValues = [...value, ...safeCustomValues]
    .map((user) => user.id.toString())
    .filter((id, index, array) => array.indexOf(id) === index);

  return (
    <MultiSelect
      label="Users"
      placeholder={placeholder}
      disabled={disabled}
      data={data}
      value={toMultiSelectValue(selectedValues, data)}
      onChange={handleChange}
      searchable
      clearable
      nothingFoundMessage="No users found"
      {...multiSelectProps}
    />
  );
};
